import { streamRng } from '../math/rng.js';
import { BRAIN, STATE } from '../actors/ai.js';

/**
 * @file THE WANTED LEVEL: one number that turns the city against you, and the
 * police response that number buys.
 *
 * Two halves, and the split is the same one `game/factions.js` makes:
 *
 *  • **HEAT is arithmetic.** A float in `0..WANTED.maxHeat`, fed by the
 *    `'crime'` bus channel, cooled by a delay and a rate. `stars = floor(heat)`.
 *    It touches no actor, no car and no renderer, which is why every number in
 *    it is proved in node (`tests/wanted.test.mjs`) rather than in a browser.
 *  • **The RESPONSE is a budget.** {@link RESPONSE_BUDGET} says how many
 *    officers, cars, vans and tanks a star level is worth, and the director
 *    below spends and un-spends it — up AND down, because "the heat dropped"
 *    has to be visible in the street and not only in the HUD.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO THREE.JS, NO LEVEL, NO ENGINE — AND NO CONTENT
 * ─────────────────────────────────────────────────────────────────────────────
 * Same deal as `traffic/Traffic.js` and `systems/Population.js`: the manager,
 * the traffic director, the population director and the faction table are all
 * INJECTED, so the whole system — the heat curve, the budgets, the pursuit
 * cadence, the deliver-and-unload, the retire — runs headless. The one piece of
 * game content it needs is WHICH def ids the law uses, and that is an option
 * ({@link LAW_IDS}) with a default, exactly like `Gameplay`'s blip prefixes.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONLY THE PLAYER'S CRIMES COUNT — AND THAT IS SOMEBODY ELSE'S GUARANTEE
 * ─────────────────────────────────────────────────────────────────────────────
 * This file adds heat for every `'crime'` it hears and does not filter. It does
 * not have to: `Population.js#attachFactionCombat` only ever emits for damage
 * the PLAYER dealt (`onDamage` returns early unless the source is
 * `factions.PLAYER`, `onKill` checks `lastAttacker`), and `Carjack.js` bills
 * `'carjack'` once per completed drag. So a gang gunning down a policeman puts
 * NOTHING on the channel and cannot raise your stars — the taxonomy is the
 * gate, and `tests/wanted.test.mjs` pins it end to end rather than restating it
 * here.
 *
 * ⚠ A LETHAL hit bills TWICE, and that is the taxonomy's shape, not a bug:
 * `EnemyManager#damage` fires `onDamage` before the kill check, so the killing
 * round emits `shot_civilian` AND `killed_civilian` — 1.8 heat, not 1.2.
 * Shooting a pedestrian to death with a 12-damage weapon therefore costs
 * 0.6 + 0.6 + (0.6 + 1.2) = 3.0, i.e. three stars for a murder in the street.
 * That is the intended reading; if you retune {@link HEAT}, retune it knowing
 * the composition.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FIVE THINGS THAT WILL BITE
 * ─────────────────────────────────────────────────────────────────────────────
 * • **The response's aggro band must COVER its own arrival ring.** The stock
 *   18 m band against a 30–46 m ring is two officers standing in a side street
 *   forever — the same trap CLAUDE.md records for the wave spawner. That is why
 *   `police_officer`/`swat`/`tank` carry a big authored `aggroRange` (48/50/90)
 *   rather than the manager default: a unit the wanted system spawned is
 *   HUNTING you, and it has to notice you from where it got out of the car.
 *
 * • **A police car is DELIVERY, not a weapon.** It drives a lane in
 *   `DRIVE_MODE.PURSUIT`, stops within {@link WANTED.arrive} metres and puts
 *   its crew on the pavement; the crew are the threat. Nothing here rams
 *   anybody — `Traffic` has no collision model for its own cars and inventing
 *   one for the police would be a physics system, not a wanted level.
 *
 * • **The car it leaves behind is a FEATURE.** An unloaded cruiser is stopped,
 *   driverless and still flashing, which makes it a plain-entry carjack target
 *   (`Population#setDriverless` is the state `Carjack.js` already knows). Take
 *   one and `Traffic#takeDriving` kills the sirens — a stolen cruiser is your
 *   car, not a police car.
 *
 * • **A retired officer becomes a PEDESTRIAN, and that is the despawn ring.**
 *   `BRAIN.CIVILIAN` + `Population#adopt` puts him on the director's books, so
 *   he strolls off and is culled at 95 m by the code that culls everybody else.
 *   No second cull, no second ring. A TANK does not get this treatment for the
 *   obvious reason and is simply despawned.
 *
 * • **Every slot reference is re-validated before it is used.** A manager slot
 *   frees on death and can be refilled by the population director inside one
 *   step, so "is this still my officer" is `isLive && team === POLICE`, checked
 *   at the top of every pass. Holding a bare index across steps is how you end
 *   up despawning a civilian.
 *
 * @module engine/systems/Wanted
 */

/** @typedef {import('../actors/EnemyManager.js').EnemyManager} EnemyManager */
/** @typedef {import('./Population.js').FactionRules} FactionRules */
/** @typedef {import('../traffic/Traffic.js').Traffic} Traffic */

/**
 * WHAT EACH CRIME IS WORTH, in heat. Keys are the `'crime'` channel's `type`
 * verbatim (`Population.js#attachFactionCombat`'s taxonomy plus `Carjack.js`'s
 * one non-damage verb), so an unknown type scores zero and cannot silently
 * bill something the taxonomy never meant.
 *
 * The shape of the table is the design: hurting a CIVILIAN costs more than
 * hurting a gang member (the city cares about bystanders and does not much care
 * about a feud it was already losing), killing costs about double wounding, and
 * the law is worth roughly twice a civilian in both verbs. `carjack` sits
 * between a wounding and a killing: it is the loudest thing you can do without
 * firing a shot.
 */
export const HEAT = Object.freeze({
  shot_civilian: 0.6,
  killed_civilian: 1.2,
  shot_gang: 0.25,
  killed_gang: 0.5,
  carjack: 0.7,
  shot_police: 1.0,
  killed_police: 1.8,
});

/** The tuning the heat curve and the response director share. */
export const WANTED = Object.freeze({
  /** Star slots. The HUD (`ui/hud/WantedStars.jsx`) draws exactly this many. */
  maxStars: 5,
  /**
   * Heat ceiling. 5.4, not 5: at exactly 5.0 the top star would drop on the
   * first decay tick after the delay, so ★5 would last 8 seconds and never feel
   * earned. The 0.4 of headroom is 2.7 s of extra ★5 on top of the delay.
   */
  maxHeat: 5.4,
  /** Seconds of no crime at all before the heat starts to fall. */
  decayDelay: 8,
  /** ...and how fast it falls, heat per second. A full 5.4 cools in ~44 s. */
  decayRate: 0.15,

  // ── the response ──
  /**
   * Hard cap on live response ACTORS (cars are not actors). The full ★5 roster
   * is 15, so this is a guard rail rather than a shaper — but it is the number
   * that stops a pathological retire/spawn cycle from filling the manager.
   */
  maxActors: 16,
  /**
   * Seconds between response SPAWNS. Without it a jump from ★0 to ★4 (one
   * grenade's worth of crime) drops eleven actors into the world on one step
   * and the frame hitches; at 0.8 s the same escalation arrives as a wave over
   * ~9 s, which is also how it reads in the fiction.
   */
  spawnInterval: 0.8,
  /** Attempts per spawn — a candidate can fail the ring, the cone or the grid. */
  tries: 8,

  // ── foot officers ──
  /**
   * The ring officers appear on, metres. Both numbers were pushed OUT from the
   * obvious ones: at the game's 34 m camera distance and 52-degree pitch the
   * player can see ~25 m behind themselves, so a 26 m ring pops a policeman on
   * screen even outside the camera cone.
   */
  footRingMin: 30,
  footRingMax: 46,
  /** Half-angle of the camera-forward cone nothing may appear inside. */
  coneDeg: 35,

  // ── cars ──
  /** Where a response car joins the network, metres from the player. */
  carRingMin: 70,
  carRingMax: 100,
  /** Seconds between pursuit repaths — how often the police re-read where you are. */
  repath: 3,
  /** Within this of the player a pursuit car stops and unloads, metres. */
  arrive: 16,
  /** ...and it will not wait longer than this for the standstill, seconds. */
  stopMax: 5,
  /**
   * A DELIVERY IS BOUNDED, and this is the number that makes it so. Seconds a
   * pursuit car may sit at a standstill — jammed behind ordinary traffic, or
   * behind another response car — before its crew give up on the ride and get
   * out where they are.
   *
   * It is not a nicety. A car that is stopped in a lane is a lane blocker, and
   * four of them converging on a player who is standing still is enough to
   * deadlock a 108 m grid: measured on the shipped city, a stationary ★5 stalled
   * 20 of 20 cars and dropped junction crossings from 833 to 131 over five
   * minutes. Bounding the ride turns every blocker into a temporary one, and it
   * reads exactly right — the police abandon the car and come on foot.
   */
  stuckMax: 4,
  /** ...and a hard ceiling on the whole ride, seconds, for the same reason. */
  driveMax: 28,
  /** Star level at which PURSUIT gains the junction queue-jump. */
  claimOverrideStar: 3,
  /** Siren alternation, Hz. 2.6 is fast enough to read as a siren at 52 degrees. */
  sirenHz: 2.6,
  /** How far a crew member is placed out from the door it came through. */
  unloadOut: 1.5,
  /** ...and how far behind a van the third trooper appears (the rear doors). */
  vanRear: 1.3,
  /**
   * How far the emptied car is slid to its RIGHT — the kerb, in right-hand
   * traffic — as it stops being an AI car. 2.6 m takes it from the lane
   * centre-line to roughly where the parked rows sit; the point is resolved
   * against the collision grid first, so a kerb with a wall on it simply leaves
   * the car in the road.
   */
  pullOver: 2.6,

  // ── the tank ──
  tankRingMin: 60,
  tankRingMax: 80,

  // ── retirement ──
  /**
   * A retired officer is despawned once he is this far from the player, or
   * after {@link retireMax} seconds, whichever comes first. He walks pavements
   * as an ordinary pedestrian in between, so the player sees the response
   * DISPERSE rather than blink out.
   */
  retireDespawn: 55,
  retireMax: 25,
});

/**
 * WHAT EACH STAR BUYS. Indexed by star level, 0..5; every entry is absolute
 * (not a delta), so the director can read one row and reconcile in both
 * directions.
 *
 * The ★5 row is 4 + 2x2 + 2x3 + 1 = 15 actors and 4 vehicles. That is the worst
 * case this game ever renders, and it is deliberately just under
 * {@link WANTED.maxActors}.
 */
export const RESPONSE_BUDGET = Object.freeze([
  Object.freeze({ officers: 0, cars: 0, vans: 0, tanks: 0 }),
  Object.freeze({ officers: 2, cars: 0, vans: 0, tanks: 0 }),
  Object.freeze({ officers: 2, cars: 1, vans: 0, tanks: 0 }),
  Object.freeze({ officers: 4, cars: 2, vans: 0, tanks: 0 }),
  Object.freeze({ officers: 4, cars: 2, vans: 2, tanks: 0 }),
  Object.freeze({ officers: 4, cars: 2, vans: 2, tanks: 1 }),
]);

/** The categories the budget is spent in, in reconcile order. */
export const RESPONSE_KINDS = Object.freeze(['officers', 'cars', 'vans', 'tanks']);

/**
 * The star level that authorises the Nth unit of each category, DERIVED from
 * {@link RESPONSE_BUDGET} so the two can never drift. `TIER_OF.cars[0] === 2`
 * reads "the first police car is a two-star unit".
 *
 * This is what makes a star DROP visible: every unit remembers its tier, and
 * the director retires anything whose tier is above the live level. No unit
 * needs to know why it is leaving.
 * @type {Readonly<Object<string, number[]>>}
 */
export const TIER_OF = Object.freeze(Object.fromEntries(RESPONSE_KINDS.map((kind) => {
  /** @type {number[]} */
  const tiers = [];
  for (let level = 0; level < RESPONSE_BUDGET.length; level++) {
    while (tiers.length < RESPONSE_BUDGET[level][kind]) tiers.push(level);
  }
  return [kind, Object.freeze(tiers)];
})));

/**
 * The law's def ids. GAME CONTENT with a default — the same compromise
 * `Gameplay.js#blipKindForId` already makes, and overridable through
 * `createWanted({law})` so a different roster never needs an engine edit.
 */
export const LAW_IDS = Object.freeze({
  officer: 'police_officer',
  swat: 'swat',
  tank: 'tank',
});

const DEG = Math.PI / 180;

/**
 * @param {string} type A `'crime'` channel type.
 * @returns {number} Heat, or 0 for anything the taxonomy does not name.
 */
export function heatFor(type) {
  const w = HEAT[type];
  return w > 0 ? w : 0;
}

/**
 * @param {number} heat
 * @returns {number} Stars, 0..{@link WANTED.maxStars}. PURE — the HUD, the
 *   response and the tests all derive the level through this one function.
 */
export function starsFor(heat) {
  if (!(heat > 0)) return 0;
  const n = Math.floor(heat);
  return n > WANTED.maxStars ? WANTED.maxStars : n;
}

/**
 * @param {number} level
 * @returns {{officers: number, cars: number, vans: number, tanks: number}}
 */
export function budgetFor(level) {
  const i = level < 0 ? 0 : level >= RESPONSE_BUDGET.length ? RESPONSE_BUDGET.length - 1 : level | 0;
  return RESPONSE_BUDGET[i];
}

// ─────────────────────────────────────────────────────────────────────────────
// The police response
// ─────────────────────────────────────────────────────────────────────────────

/**
 * THE DIRECTOR. Owns every police unit in the world and nothing else: it never
 * touches heat, never emits on the bus and never decides what a star means.
 * {@link createWanted} hands it a level and it reconciles.
 *
 * @param {Object} o
 * @param {EnemyManager} o.manager
 * @param {Array<{id?: string}>} o.types The manager's type table.
 * @param {FactionRules} o.factions
 * @param {any} o.population `createPopulation`'s system — pavements, `seatCrew`,
 *   `adopt`, `alarm`.
 * @param {Traffic} o.traffic
 * @param {any} [o.collision] `CollisionGrid`; spawns test `blocked()`.
 * @param {(x: number, z: number) => number} [o.heightAt]
 * @param {import('../types.js').Bounds2|null} [o.bounds]
 * @param {string|number} [o.seed='gameplay']
 * @param {Object} [o.law] Overrides {@link LAW_IDS}.
 * @param {Partial<typeof WANTED>} [o.tune]
 * @returns {Object}
 */
export function createPoliceResponse(o) {
  const {
    manager,
    types = [],
    factions,
    population,
    traffic,
    collision = null,
    heightAt = () => 0,
    bounds = null,
    seed = 'gameplay',
    law = LAW_IDS,
    tune = null,
  } = o ?? {};

  if (!manager) throw new Error('createPoliceResponse: a manager is required');
  if (!factions) throw new Error('createPoliceResponse: a FactionRules table is required');
  if (!population) throw new Error('createPoliceResponse: a population director is required');
  if (!traffic) throw new Error('createPoliceResponse: a Traffic director is required');

  const W = tune ? { ...WANTED, ...tune } : WANTED;
  /** The law's faction id, asked of the INJECTED table rather than hard-coded. */
  const POLICE = factions.factionForId(law.officer ?? LAW_IDS.officer);

  // ── type routing ──────────────────────────────────────────────────────────
  /** @param {string} id @returns {number} Type index, or -1 when not loaded. */
  const typeOf = (id) => types.findIndex((t) => t?.id === id);
  const officerType = typeOf(law.officer ?? LAW_IDS.officer);
  const swatType = typeOf(law.swat ?? LAW_IDS.swat);
  const tankType = typeOf(law.tank ?? LAW_IDS.tank);

  // A roster that never loaded the law can still run this director — it simply
  // has nothing to spend. That is what keeps a level with no police art from
  // being a crash instead of a quiet city.
  const carArch = traffic.archIndexOf('police');
  const vanArch = traffic.archIndexOf('swat');

  /** A DEDICATED stream (rule 4). Nothing else may draw from it. */
  const rng = streamRng(seed, 'wanted');

  // ── the books ─────────────────────────────────────────────────────────────
  /**
   * @typedef {Object} FootUnit
   * @property {number} slot Manager slot.
   * @property {number} tier Star level that authorised it.
   * @property {'officers'|'crew'} role `crew` came out of a vehicle and is not
   *   counted against the foot-officer budget — it is the vehicle's payload.
   * @property {number} from The {@link CarUnit} `id` that delivered it, or -1.
   *   NOT its tier: two vans share tier 4, and retiring one of them must not
   *   take the other one's squad off the street with it.
   */
  /** @type {FootUnit[]} In spawn order; excess retires from the END. */
  const foot = [];

  /**
   * @typedef {Object} CarUnit
   * @property {number} id Monotonic; the handle a crew member is tagged with.
   * @property {number} car Driving slot.
   * @property {number} handle Fleet handle, REMEMBERED — once the slot is gone
   *   (jacked, recycled) there is no way back to it, and the sirens have to be
   *   switchable off from here.
   * @property {'cars'|'vans'} kind
   * @property {number} tier
   * @property {string} phase `'drive' | 'stop' | 'spent'`.
   * @property {number} repathT @property {number} stopT
   */
  /** @type {CarUnit[]} */
  const vehicles = [];
  /**
   * Crewless cars left at the scene: PARKED slots now, still flashing, enterable
   * with a plain F, and not response units — the budget has already spent them.
   * @type {Array<{parked: number, handle: number}>}
   */
  const dumped = [];
  /** @type {number[]} Tank manager slots. */
  const tanks = [];
  /** @type {Array<{slot: number, t: number}>} Walking off, not yet despawned. */
  const retiring = [];

  let level = 0;
  let clock = 0;
  let spawnAcc = 0;
  let repaths = 0;
  let deployed = 0;
  let nextId = 1;
  let disposed = false;
  const focusPt = { x: 0, z: 0 };
  const world = { x: 0, y: 0, z: 0, yaw: 0 };
  const probe = { x: 0, z: 0 };
  const cosCone = Math.cos(W.coneDeg * DEG);
  let dirX = 0;
  let dirZ = -1;

  /**
   * CANDIDATE SCRATCH, allocated once. A spawn attempt FILTERS to the ring
   * first and then draws, rather than drawing from the whole city and hoping —
   * which is what it did in the first cut, and it is worth spelling out because
   * the failure was silent and expensive: the pavement graph is ~1200 nodes
   * over a 320 m island and only a few dozen sit in a 30-46 m annulus, so eight
   * uniform draws almost never found one and the whole response arrived at a
   * third of the rate the budget said it should.
   *
   * The filter is O(nodes) and runs at most once per {@link WANTED.spawnInterval},
   * i.e. barely at all.
   */
  const candidates = new Int32Array(Math.max(
    64,
    population.pavement?.n ?? 0,
    traffic.graph.laneEdges.length,
  ));

  // ── validity ──────────────────────────────────────────────────────────────

  /**
   * Is this slot STILL one of ours? A manager slot frees on death and can be
   * refilled by the population director inside a single step, so a bare index
   * held across steps is a civilian waiting to be despawned by mistake. The
   * team byte is the cheap discriminator: our units are the only POLICE in the
   * world (nothing ambient spawns the faction), and a retired officer keeps his
   * team even after his brain becomes CIVILIAN.
   * @param {number} slot
   */
  const ours = (slot) => slot >= 0 && manager.isLive(slot) && manager.team[slot] === POLICE;

  /** @param {number} slot @returns {boolean} Still fighting (not dying). */
  const fighting = (slot) => ours(slot) && manager.state[slot] !== STATE.DYING;

  // ── placement helpers ─────────────────────────────────────────────────────

  /**
   * Ring distance, camera cone, level bounds, collision — cheapest rejection
   * first, exactly like `Population#legal`.
   * @param {number} x @param {number} z @param {number} r Actor/vehicle radius.
   * @param {number} min @param {number} max
   */
  const legal = (x, z, r, min, max) => {
    const dx = x - focusPt.x;
    const dz = z - focusPt.z;
    const d = Math.hypot(dx, dz);
    if (d < min || d > max) return false;
    if (d > 1e-6 && (dx / d) * dirX + (dz / d) * dirZ > cosCone) return false;
    const b = bounds;
    if (b && (x < b.minX + r || x > b.maxX - r || z < b.minZ + r || z > b.maxZ - r)) return false;
    if (collision?.blocked?.(x, z, r)) return false;
    return true;
  };

  /**
   * Pushes a point out of the world. A policeman who gets out of a car into a
   * shopfront is the sort of thing a player screenshots.
   * @param {{x: number, z: number}} pt @param {number} r
   */
  const resolve = (pt, r) => {
    if (!collision || typeof collision.resolveCircle !== 'function') return;
    probe.x = pt.x;
    probe.z = pt.z;
    const res = collision.resolveCircle(probe, r, 2);
    pt.x = res.x;
    pt.z = res.z;
  };

  // ── spawning ──────────────────────────────────────────────────────────────

  /**
   * One foot officer, on a pavement waypoint in the ring. The pavement graph is
   * the population director's — a policeman arriving in the middle of a
   * carriageway is a policeman the traffic then brakes for.
   * @param {number} tier
   * @returns {boolean}
   */
  const spawnFoot = (tier) => {
    if (officerType < 0) return false;
    const pav = population.pavement;
    if (!pav?.n) return false;
    const r = manager.types[officerType]?.radius ?? 0.4;
    // Filter to the ring FIRST — see the `candidates` note.
    let n = 0;
    const lo = W.footRingMin * W.footRingMin;
    const hi = W.footRingMax * W.footRingMax;
    for (let i = 0; i < pav.n; i++) {
      const dx = pav.x[i] - focusPt.x;
      const dz = pav.z[i] - focusPt.z;
      const d2 = dx * dx + dz * dz;
      if (d2 >= lo && d2 <= hi) candidates[n++] = i;
    }
    if (!n) return false;
    for (let k = 0; k < W.tries; k++) {
      const node = candidates[Math.min(n - 1, (rng() * n) | 0)];
      const x = pav.x[node];
      const z = pav.z[node];
      if (!legal(x, z, r, W.footRingMin, W.footRingMax)) continue;
      const slot = manager.spawn(officerType, x, z, heightAt(x, z), POLICE);
      if (slot < 0) return false;
      foot.push({ slot, tier, role: 'officers', from: -1 });
      return true;
    }
    return false;
  };

  /**
   * One vehicle, joining the lane network out of sight and immediately in
   * pursuit. RESERVED, so the population director puts nobody in it, and then
   * crewed through {@link population.seatCrew} — which is the same registration
   * an ambient driver gets, so the seat loop, the car cull and a CARJACK of a
   * crewed cruiser all work with no special case.
   *
   * @param {'cars'|'vans'} kind @param {number} tier
   * @returns {boolean}
   */
  const spawnVehicle = (kind, tier) => {
    const arch = kind === 'cars' ? carArch : vanArch;
    const crewType = kind === 'cars' ? officerType : swatType;
    if (arch < 0 || crewType < 0) return false;
    const g = traffic.graph;
    const n = laneRing(W.carRingMin, W.carRingMax, null);
    if (!n) return false;

    for (let k = 0; k < W.tries; k++) {
      const eid = candidates[Math.min(n - 1, (rng() * n) | 0)];
      const e = g.edges[eid];
      if (!e || e.length < 12) continue;
      const s = 4 + rng() * (e.length - 8);
      g.sampleAt(eid, s, world);
      // No cone test: a car 70-100 m out is behind buildings whichever way the
      // camera looks, and rejecting the forward half here halves the supply on
      // a grid whose lanes are mostly two axes.
      const d = Math.hypot(world.x - focusPt.x, world.z - focusPt.z);
      if (d < W.carRingMin || d > W.carRingMax) continue;

      const car = traffic.spawnAt(eid, s, { arch, reserved: true });
      if (car < 0) return false;
      population.seatCrew(car, 0, crewType, POLICE);
      population.seatCrew(car, 1, crewType, POLICE);
      const rec = {
        id: nextId++,
        car,
        handle: traffic.drivingHandle(car),
        kind,
        tier,
        phase: 'drive',
        repathT: 0,
        stopT: 0,
        stuckT: 0,
        age: 0,
      };
      vehicles.push(rec);
      traffic.setPursuit(car, focusPt.x, focusPt.z, level >= W.claimOverrideStar);
      repaths++;
      return true;
    }
    return false;
  };

  /**
   * The army. A boulevard point 60-80 m out, because a tank stepping out of a
   * side street is a tank the player never sees coming and cannot run from.
   * @param {number} tier @returns {boolean}
   */
  const spawnTank = (tier) => {
    void tier;
    if (tankType < 0) return false;
    const g = traffic.graph;
    const r = manager.types[tankType]?.radius ?? 0.9;
    // Two passes: boulevards first, then any lane. A seed whose boulevards all
    // sit the wrong side of the player must still be able to send the army.
    for (let pass = 0; pass < 2; pass++) {
      const n = laneRing(W.tankRingMin, W.tankRingMax, pass === 0 ? 'boulevard' : null);
      for (let k = 0; k < W.tries && n > 0; k++) {
        const eid = candidates[Math.min(n - 1, (rng() * n) | 0)];
        const e = g.edges[eid];
        if (!e) continue;
        g.sampleAt(eid, e.length * 0.5, world);
        if (!legal(world.x, world.z, r, W.tankRingMin, W.tankRingMax)) continue;
        const slot = manager.spawn(tankType, world.x, world.z, heightAt(world.x, world.z), POLICE);
        if (slot < 0) return false;
        tanks.push(slot);
        return true;
      }
    }
    return false;
  };

  /**
   * Lane edges whose MIDPOINT sits in a ring around the focus, written into
   * {@link candidates}. Same filter-then-draw rule as {@link spawnFoot}, and it
   * matters more here: 174 lanes over a 320 m island, of which a 70-100 m
   * annulus holds a couple of dozen.
   * @param {number} min @param {number} max
   * @param {string|null} cls Road class to require, or null for any.
   * @returns {number} How many candidates were written.
   */
  function laneRing(min, max, cls) {
    const g = traffic.graph;
    const lanes = g.laneEdges;
    const lo = min * min;
    const hi = max * max;
    let n = 0;
    for (let i = 0; i < lanes.length; i++) {
      const e = g.edges[lanes[i]];
      if (!e || (cls && e.cls !== cls)) continue;
      // The midpoint, sampled through the same path everything else uses — the
      // lane is short enough that its middle is representative.
      g.sampleAt(e.id, e.length * 0.5, world);
      const dx = world.x - focusPt.x;
      const dz = world.z - focusPt.z;
      const d2 = dx * dx + dz * dz;
      if (d2 >= lo && d2 <= hi) candidates[n++] = e.id;
    }
    return n;
  }

  // ── deliver and unload ────────────────────────────────────────────────────

  /**
   * Turns a SEATED crew member into a fighter on the tarmac beside its door.
   * `setBrain(NONE)` is the whole conversion — it puts the slot back in the
   * fighter step and back into separation — and the position is the door it
   * came through, pushed one stride clear so two crew do not land inside each
   * other.
   * @param {number} slot @param {CarUnit} rec @param {number} seat
   */
  const deploy = (slot, rec, seat) => {
    if (!ours(slot)) return;
    const car = rec.car;
    const w = traffic.getDoorWorld(car, seat, world);
    if (w) {
      // Out from the car's centre, not out from the door: the door anchor is
      // already on the flank, so the same direction just widens the step.
      const ax = w.x - traffic.x[car];
      const az = w.z - traffic.z[car];
      const l = Math.hypot(ax, az) || 1;
      probe.x = w.x + (ax / l) * W.unloadOut;
      probe.z = w.z + (az / l) * W.unloadOut;
      resolve(probe, manager.radius[slot] ?? 0.4);
      manager.x[slot] = probe.x;
      manager.z[slot] = probe.z;
      manager.y[slot] = heightAt(probe.x, probe.z);
    }
    manager.setBrain(slot, BRAIN.NONE);
    foot.push({ slot, tier: rec.tier, role: 'crew', from: rec.id });
    deployed++;
  };

  /**
   * The crew get out. Both seats through the population seam (so the director
   * stops slaving them), plus — for a VAN — a third trooper out of the BACK.
   *
   * ⚠ That third man is not a seat and never was. `Traffic.SEAT` defines two,
   * and they are the two the camera can see through the windscreen; a riot van
   * carries its squad in the load bay, which is exactly where he appears.
   * @param {CarUnit} rec
   */
  const unload = (rec) => {
    for (const seat of [0, 1]) {
      const slot = population.releaseOccupant(rec.car, seat);
      if (slot >= 0) deploy(slot, rec, seat);
    }
    if (rec.kind === 'vans' && swatType >= 0) {
      const yaw = traffic.yaw[rec.car];
      const back = -(traffic.archChassis[traffic.arch[rec.car]]?.L ?? 4.7) / 2 - W.vanRear;
      probe.x = traffic.x[rec.car] + back * Math.cos(yaw);
      probe.z = traffic.z[rec.car] - back * Math.sin(yaw);
      resolve(probe, manager.types[swatType]?.radius ?? 0.4);
      const slot = manager.spawn(swatType, probe.x, probe.z, heightAt(probe.x, probe.z), POLICE);
      if (slot >= 0) {
        foot.push({ slot, tier: rec.tier, role: 'crew', from: rec.id });
        deployed++;
      }
    }
    // ── the car is spent, and it must STOP BEING AN AI CAR ──────────────────
    // The first cut left it in the driving pool, stopped and driverless, on the
    // grounds that "an abandoned cruiser in the road" is both a jackable prize
    // and good scenery. It is neither: a permanently held car is a permanent
    // lane blocker, every car behind it queues forever, the queue backs into a
    // junction and "don't block the box" then refuses every grant. Measured on
    // the shipped city — 833 junction crossings in five minutes of ordinary
    // traffic against 146 with ONE cruiser parked in a lane, and 20 of 20 cars
    // stopped at the end of it. The city deadlocked.
    //
    // So it becomes a real PARKED car through `takeDriving` — the one handover
    // path that already exists. Out of the driving pool, so nothing can queue
    // behind it; collision circles, so the player walks round it; a plain-entry
    // carjack target (better than the driverless-driving-car path, not worse);
    // and it rides the GUEST parked reserve, which evicts the furthest one, so
    // a long siege cannot litter the island.
    const x = traffic.x[rec.car];
    const z = traffic.z[rec.car];
    const yaw = traffic.yaw[rec.car];
    traffic.clearPursuit(rec.car);
    traffic.pin(rec.car, false);
    const slot = traffic.takeDriving(rec.car);
    if (slot >= 0) {
      // ...and pull it over to the kerb on the way. `Ry(yaw)` maps the nose to
      // (cos, -sin), so the RIGHT of travel is (-(-sin), cos) = (sin, cos).
      probe.x = x + Math.sin(yaw) * W.pullOver;
      probe.z = z + Math.cos(yaw) * W.pullOver;
      resolve(probe, (traffic.archChassis[traffic.pArch[slot]]?.W ?? 1.8) / 2);
      traffic.placeParked(slot, probe.x, heightAt(probe.x, probe.z), probe.z, yaw);
      // The lights stay on: a cruiser abandoned at a scene is still a scene.
      // `takeDriving` cleared them (that is what a JACK wants), so put them
      // back — and the blink below keeps them alternating until the player
      // takes the car or the ring collects it.
      //
      // ⚠ Drop any entry already pointing at this PARKED slot first. The guest
      // reserve evicts its furthest car to make room, so `takeDriving` can hand
      // back a slot this list is still holding, and two entries for one car
      // means the count in `stats()` climbs past the six slots that exist.
      for (let k = dumped.length - 1; k >= 0; k--) if (dumped[k].parked === slot) dumped.splice(k, 1);
      dumped.push({ parked: slot, handle: traffic.parkedHandle(slot) });
    }
    population.alarm?.(x, z, W.arrive);
  };

  // ── retirement ────────────────────────────────────────────────────────────

  /** @param {number} handle @param {number} r @param {number} b */
  const siren = (handle, r, b) => {
    if (handle < 0) return;
    traffic.fleet?.setLamps?.(handle, { sirenR: r, sirenB: b });
  };

  /**
   * A person stops being police business. He keeps his faction (so shooting him
   * is still `shot_police`) but loses the brain that made him a fighter, and
   * the ambient director takes over his errands and his cull.
   * @param {FootUnit} u @param {boolean} immediate
   */
  const retireFoot = (u, immediate) => {
    if (!ours(u.slot)) return;
    if (immediate) {
      manager.despawn(u.slot);
      return;
    }
    manager.setBrain(u.slot, BRAIN.CIVILIAN);
    population.adopt(u.slot);
    retiring.push({ slot: u.slot, t: 0 });
  };

  /**
   * A vehicle stops being police business: lights off, pursuit off, pin off. It
   * is an ordinary car with its crew still aboard from here, so it drives away
   * and the despawn ring retires it — crew included, through the population
   * director's own `onDeactivate`. That IS the choreography: the response
   * leaves the way it arrived.
   *
   * `immediate` is the WASTED respawn, and it cannot use that story: a cruiser
   * with two policemen in it still driving the block the player wakes up on is
   * not "you got away". The crew are taken off the books and despawned, and the
   * car is left stopped and driverless — a state the world already holds, and
   * one the player can come back and take.
   * @param {CarUnit} rec @param {boolean} immediate
   */
  const retireVehicle = (rec, immediate) => {
    siren(rec.handle, 0, 0);
    if (!traffic.isDriving(rec.car)) return;
    traffic.clearPursuit(rec.car);
    traffic.pin(rec.car, false);
    if (!immediate) {
      traffic.releaseStop(rec.car);
      return;
    }
    for (const seat of [0, 1]) {
      const slot = population.releaseOccupant(rec.car, seat);
      if (slot >= 0 && manager.isLive(slot)) manager.despawn(slot);
    }
    population.setDriverless(rec.car);
  };

  /**
   * THE STAR-DROP RULE, and the only one that has to be right: every PERSON
   * remembers the star level that authorised them, so anything above the live
   * level walks away. It subsumes the officer head-count and — the case a
   * per-category count cannot see — the crew of a vehicle that has already
   * unloaded and left the books.
   * @param {boolean} immediate
   */
  const retireStale = (immediate) => {
    for (let k = foot.length - 1; k >= 0; k--) {
      if (foot[k].tier <= level) continue;
      retireFoot(foot[k], immediate);
      foot.splice(k, 1);
    }
  };

  /**
   * THE CAP, made an invariant rather than a gate.
   *
   * A budget row names four officers and four vehicles, but a vehicle DELIVERS
   * and leaves, so its crew stay on the pavement while the budget immediately
   * buys another vehicle — and over a long siege the crew accumulate. Capping
   * only the SPAWN leaves the count creeping past `maxActors` by the payload of
   * whatever was already in flight (measured: 17 against a 16 cap over five
   * minutes at ★5).
   *
   * So the excess is retired instead, OLDEST CREW FIRST. That is the right unit
   * to drop on both counts: the ring-spawned officers are the ones the budget
   * actually names, and rolling the delivered squads reads as a siege being
   * relieved rather than as police vanishing.
   */
  const retireOverCap = () => {
    while (actorCount() > W.maxActors) {
      let k = foot.findIndex((u) => u.role === 'crew');
      if (k < 0) k = foot.length - 1;
      if (k < 0) return;
      retireFoot(foot[k], false);
      foot.splice(k, 1);
    }
  };

  /**
   * Reconciles ONE category down to `want`, retiring the most recently
   * authorised first — which is the same as retiring the highest tier, because
   * units are only ever appended.
   * @param {string} kind @param {number} want @param {boolean} immediate
   */
  const retireDown = (kind, want, immediate) => {
    if (kind === 'tanks') {
      // A tank does not stroll a pavement. It goes.
      while (tanks.length > want) {
        const slot = tanks.pop();
        if (ours(slot)) manager.despawn(slot);
      }
      return;
    }
    if (kind === 'officers') {
      let have = 0;
      for (const u of foot) if (u.role === 'officers') have++;
      for (let i = foot.length - 1; i >= 0 && have > want; i--) {
        if (foot[i].role !== 'officers') continue;
        retireFoot(foot[i], immediate);
        foot.splice(i, 1);
        have--;
      }
      return;
    }
    // A vehicle's CREW go with it, wherever they are: still aboard (they drive
    // off with it) or already on the pavement (they walk off). The tag is the
    // vehicle's ID and not its tier, because two vans share tier 4 and one
    // retiring van must not take the other's squad with it.
    let have = 0;
    for (const v of vehicles) if (v.kind === kind) have++;
    for (let i = vehicles.length - 1; i >= 0 && have > want; i--) {
      if (vehicles[i].kind !== kind) continue;
      const rec = vehicles[i];
      retireVehicle(rec, immediate);
      retireCrewOf(rec.id, immediate);
      vehicles.splice(i, 1);
      have--;
    }
  };

  /**
   * Every crew member a given vehicle put on the street.
   * @param {number} id @param {boolean} immediate
   */
  const retireCrewOf = (id, immediate) => {
    for (let k = foot.length - 1; k >= 0; k--) {
      if (foot[k].role !== 'crew' || foot[k].from !== id) continue;
      retireFoot(foot[k], immediate);
      foot.splice(k, 1);
    }
  };

  // ── the step ──────────────────────────────────────────────────────────────

  /** Drops references to anything the world has taken back. */
  const prune = () => {
    // A dead officer is nobody's, and his slot can be refilled by the ambient
    // director on the very next step — `fighting` is the `isLive && POLICE`
    // check that stops us from inheriting whoever lands there.
    for (let i = foot.length - 1; i >= 0; i--) if (!fighting(foot[i].slot)) foot.splice(i, 1);
    for (let i = tanks.length - 1; i >= 0; i--) if (!fighting(tanks[i])) tanks.splice(i, 1);
    for (let i = vehicles.length - 1; i >= 0; i--) {
      if (traffic.isDriving(vehicles[i].car)) continue;
      // Jacked, or recycled. The slot is gone; the sirens are reached through
      // the handle we kept — and `takeDriving` has already cleared them for a
      // jack, so this only matters for a retire.
      siren(vehicles[i].handle, 0, 0);
      vehicles.splice(i, 1);
    }
    // A dumped cruiser stops being ours the moment the player gets into it or
    // the guest reserve recycles it. Either way the lights go out — a stolen
    // police car is your car.
    for (let i = dumped.length - 1; i >= 0; i--) {
      if (traffic.isParked(dumped[i].parked)) continue;
      siren(dumped[i].handle, 0, 0);
      dumped.splice(i, 1);
    }
  };

  /** @returns {number} Live response ACTORS (vehicles are not actors). */
  const actorCount = () => foot.length + tanks.length + vehicles.length * 2;

  /**
   * One fixed step.
   * @param {number} dt
   * @param {{x: number, z: number}} focus The player.
   * @param {number} [camYaw=0] Live rig yaw — the spawn cone needs it.
   */
  const update = (dt, focus, camYaw = 0) => {
    if (disposed || !(dt > 0) || !focus) return;
    clock += dt;
    focusPt.x = focus.x;
    focusPt.z = focus.z;
    dirX = -Math.sin(camYaw);
    dirZ = -Math.cos(camYaw);

    prune();

    // ── 1. reconcile DOWN. Always, and before anything is spent: a star drop
    //      has to be visible on the step it happens.
    const budget = budgetFor(level);
    retireStale(false);
    for (const kind of RESPONSE_KINDS) retireDown(kind, budget[kind], false);

    // ── 2. pursuits ────────────────────────────────────────────────────────
    const priority = level >= W.claimOverrideStar;
    const alt = ((clock * W.sirenHz) % 1) < 0.5;
    for (const rec of vehicles) {
      siren(rec.handle, alt ? 1 : 0, alt ? 0 : 1);
      const dx = traffic.x[rec.car] - focusPt.x;
      const dz = traffic.z[rec.car] - focusPt.z;
      const near = dx * dx + dz * dz <= W.arrive * W.arrive;

      if (rec.phase === 'drive') {
        rec.age += dt;
        rec.stuckT = traffic.v[rec.car] < 0.2 ? rec.stuckT + dt : 0;
        rec.repathT += dt;
        if (rec.repathT >= W.repath) {
          rec.repathT -= W.repath;
          traffic.setPursuit(rec.car, focusPt.x, focusPt.z, priority);
          repaths++;
        } else if (traffic.priority[rec.car] !== (priority ? 1 : 0)) {
          // The queue-jump follows the LIVE star level, not the level the car
          // was dispatched at — a drop from ★3 has to take the blue-light
          // privilege away now, not at the next repath.
          traffic.priority[rec.car] = priority ? 1 : 0;
        }
        // Arrived, jammed, or simply too long on the road: all three end the
        // ride. See `WANTED.stuckMax` for why the last two are not optional.
        if (near || rec.stuckT >= W.stuckMax || rec.age >= W.driveMax) {
          rec.phase = 'stop';
          rec.stopT = 0;
          traffic.requestStop(rec.car);
        }
      } else {
        rec.stopT += dt;
        if (traffic.isStopped(rec.car) || rec.stopT >= W.stopMax) {
          unload(rec);
          rec.phase = 'spent';
        }
      }
    }
    // The spent ones leave the books here rather than mid-iteration.
    for (let i = vehicles.length - 1; i >= 0; i--) if (vehicles[i].phase === 'spent') vehicles.splice(i, 1);
    for (const d of dumped) siren(d.handle, alt ? 1 : 0, alt ? 0 : 1);

    // ── 3. reconcile UP, one unit per `spawnInterval` ───────────────────────
    spawnAcc += dt;
    if (spawnAcc >= W.spawnInterval) {
      let officers = 0;
      for (const u of foot) if (u.role === 'officers') officers++;
      let cars = 0;
      let vans = 0;
      for (const v of vehicles) (v.kind === 'cars' ? (cars++) : (vans++));
      // One category per tick. THE TANK IS FIRST, and that is not an aesthetic
      // choice: everything else in the response replaces itself (a car unloads,
      // its crew stay on the pavement, the budget buys another car), so the
      // actor count creeps up to the cap and the LAST thing in the order is
      // never bought at all. Observed in the browser — a five-star siege that
      // ran for a minute with fifteen police on the street and no tank, which
      // is the one unit ★5 is actually about. It is also exempt from the cap
      // below, because it is a single actor the budget already caps at one, and
      // `retireOverCap` makes room for it by sending a foot officer home.
      //
      // After that the order is the escalation order: the foot patrol is what
      // "the police are coming" means at ★1, and it must not be starved by a
      // van that keeps failing its ring test at ★4.
      /** @type {(() => boolean)|null} */
      let buy = null;
      let exempt = false;
      if (tanks.length < budget.tanks) {
        buy = () => spawnTank(TIER_OF.tanks[tanks.length] ?? level);
        exempt = true;
      } else if (officers < budget.officers) buy = () => spawnFoot(TIER_OF.officers[officers] ?? level);
      else if (cars < budget.cars) buy = () => spawnVehicle('cars', TIER_OF.cars[cars] ?? level);
      else if (vans < budget.vans) buy = () => spawnVehicle('vans', TIER_OF.vans[vans] ?? level);

      if (!buy || (!exempt && actorCount() >= W.maxActors)) {
        // Nothing to buy, or no room: stay ARMED, so the next star spends on
        // the step it is earned rather than `spawnInterval` after it.
        spawnAcc = W.spawnInterval;
      } else {
        // A failed attempt costs HALF an interval, not a whole one. The ring,
        // the cone and the collision grid can all reject a candidate, and
        // paying full price for each rejection is what made the first cut
        // deliver a four-officer response at a rate of about two.
        spawnAcc = buy() ? 0 : W.spawnInterval * 0.5;
      }
    }

    // ── 3b. the cap, LAST ──────────────────────────────────────────────────
    // After the unloads above, not before them: an unload moves a crew from a
    // vehicle's payload onto the pavement and that is the moment the count can
    // cross the line. Enforcing it at the top of the step would leave the world
    // one frame over the cap every time a van emptied.
    retireOverCap();

    // ── 4. the ones walking away ───────────────────────────────────────────
    for (let i = retiring.length - 1; i >= 0; i--) {
      const r = retiring[i];
      r.t += dt;
      if (!ours(r.slot)) {
        retiring.splice(i, 1);
        continue;
      }
      const dx = manager.x[r.slot] - focusPt.x;
      const dz = manager.z[r.slot] - focusPt.z;
      if (r.t >= W.retireMax || dx * dx + dz * dz >= W.retireDespawn * W.retireDespawn) {
        manager.despawn(r.slot);
        retiring.splice(i, 1);
      }
    }
  };

  return {
    name: 'police',
    /** @returns {number} */
    get level() {
      return level;
    },
    /**
     * The one input. Reconciliation happens on the next {@link update}, so a
     * caller may set it as often as it likes.
     * @param {number} next
     */
    setLevel(next) {
      level = next < 0 ? 0 : next > WANTED.maxStars ? WANTED.maxStars : next | 0;
    },
    update,

    /**
     * Stands everything down. `immediate` despawns the people where they stand
     * (the WASTED respawn — a street full of police watching you reappear at
     * the plaza is not "you got away"); otherwise they walk off.
     * @param {boolean} [immediate=false]
     */
    retireAll(immediate = false) {
      level = 0;
      for (const kind of RESPONSE_KINDS) retireDown(kind, 0, immediate);
      // ...and the crews of vehicles that have already unloaded and left the
      // books. `retireStale` at level 0 is "everybody", by construction.
      retireStale(immediate);
      for (const d of dumped) siren(d.handle, 0, 0);
      dumped.length = 0;
      if (!immediate) return;
      for (const r of retiring) if (ours(r.slot)) manager.despawn(r.slot);
      retiring.length = 0;
      // THE SWEEP, and it is a guarantee rather than more bookkeeping. A car
      // that was let go GENTLY earlier (a star dropped, so it drove off with
      // its crew) is no longer on the books, and its two policemen are still
      // sitting in it somewhere in the district — which is fine when the heat
      // merely cooled, and not fine at all on a respawn. One linear pass over
      // the pool cannot miss one, whatever route it took out of our hands.
      for (let i = 0; i < manager.top; i++) {
        if (!ours(i) || manager.brain[i] !== BRAIN.SEATED) continue;
        manager.despawn(i);
      }
    },

    /** @returns {Object} Live counters for the debug read-out. */
    stats() {
      let officers = 0;
      let crew = 0;
      for (const u of foot) (u.role === 'officers' ? (officers++) : (crew++));
      let cars = 0;
      let vans = 0;
      for (const v of vehicles) (v.kind === 'cars' ? (cars++) : (vans++));
      return {
        level,
        officers,
        crew,
        cars,
        vans,
        tanks: tanks.length,
        dumped: dumped.length,
        retiring: retiring.length,
        units: officers + crew + cars + vans + tanks.length,
        actors: actorCount(),
        repaths,
        deployed,
      };
    },

    /** Exposed for the tests + the debug panel. */
    get vehicles() {
      return vehicles;
    },
    get foot() {
      return foot;
    },
    get tanks() {
      return tanks;
    },

    /**
     * WHERE THE NEAREST FLASHING LIGHTBAR IS, if there is one at all.
     *
     * ⚠⚠ **ONE SET, TWO OUTPUTS: THE SOUND IS WHERE THE LIGHTS ARE.** The list
     * walked here is EXACTLY the list `update` flashes above — `vehicles` (the
     * cruisers and vans still driving) then `dumped` (crewless cars left at a
     * scene, lights still on) — so a siren can never be heard coming off
     * something the player cannot see flashing, and a car whose lights went out
     * (jacked, recycled, retired) goes silent in the same step it goes dark,
     * because the same two arrays answer both questions.
     *
     * That is the whole reason this reader exists rather than the audio system
     * finding a policeman for itself. It used to walk `EnemyManager` for the
     * nearest live POLICE slot — and that set is PEOPLE. {@link RESPONSE_BUDGET}
     * buys ★1 two foot officers and ZERO cars, so at one star the siren was
     * bolted to a man strolling down the pavement. A siren is a thing bolted to
     * a CAR, ★2 is the first car, and ★1 therefore has no siren BY DESIGN.
     *
     * ⚠ IT DELIBERATELY DOES NOT EXPOSE `dumped` AS A GETTER beside the three
     * above. The READER is the seam, not the list: a consumer handed the arrays
     * would walk them itself, and a second walk is a second answer to "where is
     * the siren" that can disagree with this one — the
     * `hurtPlayer(amount, lethal)` family.
     *
     * ⚠ STALENESS IS GUARDED HERE TOO, the same two tests {@link prune} makes.
     * `prune` runs first on every step, so they are near-always true — but this
     * is called from the AUDIO system, at a different point in the step order,
     * and a slot index the world has already taken back reads whichever car
     * landed in it instead.
     *
     * SQUARED distances (a comparison needs no sqrt — the mixer's own idiom),
     * no allocation, no dice (rule 4), nothing imported.
     *
     * @param {number} x @param {number} z The listener.
     * @param {{x: number, y: number, z: number}} out Written ONLY on a `true`;
     *   a caller's scratch is left exactly as it was when nothing is flashing.
     * @returns {boolean} Whether anything of ours has its lightbar on.
     */
    nearestSiren(x, z, out) {
      let best = Infinity;
      let bx = 0;
      let by = 0;
      let bz = 0;
      for (const rec of vehicles) {
        if (!traffic.isDriving(rec.car)) continue;
        const dx = traffic.x[rec.car] - x;
        const dz = traffic.z[rec.car] - z;
        const d2 = dx * dx + dz * dz;
        if (d2 >= best) continue;
        best = d2;
        bx = traffic.x[rec.car];
        by = traffic.y[rec.car];
        bz = traffic.z[rec.car];
      }
      for (const d of dumped) {
        if (!traffic.isParked(d.parked)) continue;
        const dx = traffic.pX[d.parked] - x;
        const dz = traffic.pZ[d.parked] - z;
        const d2 = dx * dx + dz * dz;
        if (d2 >= best) continue;
        best = d2;
        bx = traffic.pX[d.parked];
        by = traffic.pY[d.parked];
        bz = traffic.pZ[d.parked];
      }
      if (best === Infinity) return false;
      out.x = bx;
      out.y = by;
      out.z = bz;
      return true;
    },

    /** Idempotent. Frees every actor and every car this director put out. */
    dispose() {
      if (disposed) return;
      this.retireAll(true);
      for (const rec of vehicles) siren(rec.handle, 0, 0);
      vehicles.length = 0;
      foot.length = 0;
      tanks.length = 0;
      disposed = true;
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// The heat curve + the composed system
// ─────────────────────────────────────────────────────────────────────────────

/**
 * THE WANTED SYSTEM. Subscribes to `'crime'`, owns the heat, publishes
 * `'wanted:level'`, drives `FactionState#setPoliceHostile` and — when it has a
 * manager, a population and a traffic director — a {@link createPoliceResponse}.
 *
 * With only a bus and a faction table it is the heat curve alone, which is
 * exactly what the arithmetic tests want and exactly what a level with no
 * pavements and no roads should get.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BUS CONTRACT
 * ─────────────────────────────────────────────────────────────────────────────
 * | channel         | dir | payload                                    |
 * |-----------------|-----|--------------------------------------------|
 * | `crime`         | in  | `{type, x, z, faction, victim}`             |
 * | `wanted:level`  | out | `{level: 0..5, flash: boolean}` ON CHANGE ONLY |
 *
 * ⚠ Nothing is emitted at level 0 until the level has been non-zero, which is
 * what keeps `ui/hud/WantedStars.jsx` invisible in a peaceful city — it hides
 * itself until the first event ever arrives.
 *
 * @param {Object} o
 * @param {{emit: Function, on?: Function}} o.bus
 * @param {FactionRules} o.factions
 * @param {EnemyManager} [o.manager]
 * @param {Array<{id?: string}>} [o.types]
 * @param {any} [o.population]
 * @param {Traffic} [o.traffic]
 * @param {any} [o.collision]
 * @param {(x: number, z: number) => number} [o.heightAt]
 * @param {import('../types.js').Bounds2|null} [o.bounds]
 * @param {string|number} [o.seed='gameplay']
 * @param {Object} [o.law]
 * @param {Partial<typeof WANTED>} [o.tune]
 * @returns {Object}
 */
export function createWanted(o) {
  const {
    bus = null,
    factions,
    manager = null,
    types = [],
    population = null,
    traffic = null,
    collision = null,
    heightAt = () => 0,
    bounds = null,
    seed = 'gameplay',
    law = LAW_IDS,
    tune = null,
  } = o ?? {};

  if (!factions) throw new Error('createWanted: a FactionRules table is required');

  const W = tune ? { ...WANTED, ...tune } : WANTED;

  const response = manager && population && traffic
    ? createPoliceResponse({
      manager, types, factions, population, traffic, collision, heightAt, bounds, seed, law, tune,
    })
    : null;

  let heat = 0;
  let level = 0;
  /** Seconds since the last crime. Starts "long ago" so a quiet city stays quiet. */
  let sinceCrime = Infinity;
  /** Have we ever published? Until we have, level 0 is silence, not an event. */
  let published = false;
  let crimes = 0;
  let disposed = false;

  /**
   * Publishes a level change and turns the law on or off. THE only writer of
   * `level`, so "emitted exactly once per change" is structural.
   * @param {number} next
   */
  const setLevel = (next) => {
    if (next === level) return;
    const flash = next > level;
    level = next;
    published = true;
    factions.state.setPoliceHostile?.(level >= 1);
    response?.setLevel(level);
    bus?.emit?.('wanted:level', { level, flash });
  };

  /**
   * One crime. Public as well as bus-driven so a test (or a cheat) can bill one
   * without inventing a payload.
   * @param {string} type @returns {number} The heat it added.
   */
  const addCrime = (type) => {
    const w = heatFor(type);
    if (!(w > 0)) return 0;
    crimes++;
    sinceCrime = 0;
    heat = Math.min(W.maxHeat, heat + w);
    setLevel(starsFor(heat));
    return w;
  };

  const onCrime = (p) => {
    if (disposed) return;
    addCrime(typeof p === 'string' ? p : p?.type);
  };
  const off = bus?.on?.('crime', onCrime) ?? null;

  /**
   * One fixed step: cool down, republish if a threshold was crossed, then let
   * the response reconcile. In that order — a unit must never be spawned for a
   * star that expired on this very step.
   *
   * @param {number} dt
   * @param {{x: number, z: number}} [focus] The player. Without one the response
   *   has no ring to work with and only the heat runs.
   * @param {number} [camYaw=0]
   */
  const update = (dt, focus = null, camYaw = 0) => {
    if (disposed || !(dt > 0)) return;
    if (sinceCrime < W.decayDelay) sinceCrime += dt;
    else if (heat > 0) {
      heat -= W.decayRate * dt;
      if (heat < 0) heat = 0;
      setLevel(starsFor(heat));
    }
    if (focus) response?.update(dt, focus, camYaw);
  };

  /**
   * BACK TO A QUIET CITY. The WASTED respawn's one call: heat to zero, the
   * provocation timers with it, and the whole response stood down where it
   * stands (see {@link createPoliceResponse}'s `retireAll`).
   */
  const reset = () => {
    heat = 0;
    sinceCrime = Infinity;
    response?.retireAll(true);
    // The gangs forget too. Waking up at the plaza with a crew still hunting
    // you is a death loop, and `FactionState#reset` is the one statement of
    // "nobody is angry with the player".
    factions.state.reset?.();
    setLevel(0);
  };

  return {
    name: 'wanted',
    /** @returns {number} 0..{@link WANTED.maxHeat} */
    get heat() {
      return heat;
    },
    /** @returns {number} 0..5 */
    get level() {
      return level;
    },
    /** @returns {number} Seconds since the last crime. */
    get idle() {
      return sinceCrime;
    },
    /** @type {ReturnType<typeof createPoliceResponse>|null} */
    response,
    addCrime,
    update,
    reset,

    /** @returns {Object} */
    stats() {
      return {
        heat: Math.round(heat * 100) / 100,
        level,
        crimes,
        idle: sinceCrime === Infinity ? -1 : Math.round(sinceCrime * 10) / 10,
        police: response ? response.stats() : null,
      };
    },

    /** Idempotent. */
    dispose() {
      if (disposed) return;
      disposed = true;
      off?.();
      response?.dispose();
      factions.state.setPoliceHostile?.(false);
    },
  };
}
